import { UserRoleCode } from './persons/enums/user-role-code';
import { ModuleName } from './shared/enums/module-name';

const defaultRoles = [
  UserRoleCode.SuperAdministrator,
  UserRoleCode.PlatformAdministrator,
  UserRoleCode.ViewOnly
];

type moduleRoleAccessType = Record<ModuleName, UserRoleCode[]>;

const moduleRoleAccessMap:  moduleRoleAccessType= {
  [ModuleName.Communication]: [
    ...defaultRoles,
    UserRoleCode.RegulatoryComplianceAdministrator,
    UserRoleCode.JMAuctionAdministrator,
    UserRoleCode.JewelerPagesAdministrator,
    UserRoleCode.JMPointOfSaleAdministrator,
    UserRoleCode.JMMarketplaceAdministrator,
    UserRoleCode.JMPartnerGatewayAdministrator,
    UserRoleCode.JMAppraisalAdministrator,
    UserRoleCode.JMShippingRegistrationAdministrator,
    UserRoleCode.JMPersonalLinesClaimsAdministrator,
    UserRoleCode.JMCommercialLinesAdministrator,
    UserRoleCode.JMShippingAdministrator,
    UserRoleCode.JMCarePlanAdministrator
  ],
  [ModuleName.Risk]: [
    ...defaultRoles,
    UserRoleCode.RegulatoryComplianceAdministrator,
  ],
  [ModuleName.Identity]: [
    ...defaultRoles,
    UserRoleCode.JMIdentitySupportLevel1
  ],
  // JM Identity is the second live identity host (jm_identityservice), serving the same
  // identity-user administration area as the Identity tile above - same audience.
  [ModuleName.JMIdentity]: [
    ...defaultRoles,
    UserRoleCode.JMIdentitySupportLevel1
  ],
  [ModuleName.Membership]: [
    ...defaultRoles,
    UserRoleCode.JMCarePlanAdministrator,
    UserRoleCode.JMCarePlanSales,
    UserRoleCode.JMCarePlanFinance,
    UserRoleCode.JMCarePlanCallCenter,
    UserRoleCode.JMCarePlanClaimProcessor,
    UserRoleCode.JMCarePlanClaimProcessingManager,
    UserRoleCode.JMShippingAdministrator,
    UserRoleCode.JMCommercialLinesAdministrator,
    UserRoleCode.JMPersonalLinesClaimsAdministrator,
    UserRoleCode.JMPersonalLinesClaimsExaminer,
    UserRoleCode.JMPersonalLinesClaimsCallCenter,
    UserRoleCode.JMAppraisalAdministrator,
    UserRoleCode.JMPartnerGatewayAdministrator,
    UserRoleCode.PlatformSupportL1,
    UserRoleCode.JMMarketplaceAdministrator,
    UserRoleCode.JMPointOfSaleAdministrator,
    UserRoleCode.JMMembershipReportViewer,
    UserRoleCode.JMJewelerProgramsFinance,
    UserRoleCode.JewelerPagesAdministrator,
    UserRoleCode.JMAuctionAdministrator,
    UserRoleCode.RegulatoryComplianceAdministrator,
    UserRoleCode.MembershipAdministrator
  ],
  [ModuleName.Content]: [
    ...defaultRoles,
    UserRoleCode.RegulatoryComplianceAdministrator,
    UserRoleCode.JMAuctionAdministrator,
    UserRoleCode.JewelerPagesAdministrator,
    UserRoleCode.JMPointOfSaleAdministrator,
    UserRoleCode.JMMarketplaceAdministrator,
    UserRoleCode.JMPartnerGatewayAdministrator,
    UserRoleCode.JMAppraisalAdministrator,
    UserRoleCode.JMShippingRegistrationAdministrator,
    UserRoleCode.JMPersonalLinesClaimsAdministrator,
    UserRoleCode.JMCommercialLinesAdministrator,
    UserRoleCode.JMShippingAdministrator,
    UserRoleCode.JMCarePlanAdministrator
  ],
  [ModuleName.Payment]: [
    ...defaultRoles,
  ],
  [ModuleName.Document]: [
    ...defaultRoles,
    UserRoleCode.RegulatoryComplianceAdministrator,
    UserRoleCode.JMAuctionAdministrator,
    UserRoleCode.JewelerPagesAdministrator,
    UserRoleCode.JMPointOfSaleAdministrator,
    UserRoleCode.JMMarketplaceAdministrator,
    UserRoleCode.JMPartnerGatewayAdministrator,
    UserRoleCode.JMAppraisalAdministrator,
    UserRoleCode.JMShippingRegistrationAdministrator,
    UserRoleCode.JMPersonalLinesClaimsAdministrator,
    UserRoleCode.JMCommercialLinesAdministrator,
    UserRoleCode.JMShippingAdministrator,
    UserRoleCode.JMCarePlanAdministrator
  ],
  [ModuleName.Account]: [
    ...defaultRoles,
  ],
  // The real '/reporting' route is already gated on moduleRoleAccess.membership; mirrored
  // here so the health-check tile is visible to the same audience as the page it links to.
  [ModuleName.Reporting]: [
    ...defaultRoles,
    UserRoleCode.JMCarePlanAdministrator,
    UserRoleCode.JMCarePlanSales,
    UserRoleCode.JMCarePlanFinance,
    UserRoleCode.JMCarePlanCallCenter,
    UserRoleCode.JMCarePlanClaimProcessor,
    UserRoleCode.JMCarePlanClaimProcessingManager,
    UserRoleCode.JMShippingAdministrator,
    UserRoleCode.JMCommercialLinesAdministrator,
    UserRoleCode.JMPersonalLinesClaimsAdministrator,
    UserRoleCode.JMPersonalLinesClaimsExaminer,
    UserRoleCode.JMPersonalLinesClaimsCallCenter,
    UserRoleCode.JMAppraisalAdministrator,
    UserRoleCode.JMPartnerGatewayAdministrator,
    UserRoleCode.PlatformSupportL1,
    UserRoleCode.JMMarketplaceAdministrator,
    UserRoleCode.JMPointOfSaleAdministrator,
    UserRoleCode.JMMembershipReportViewer,
    UserRoleCode.JMJewelerProgramsFinance,
    UserRoleCode.JewelerPagesAdministrator,
    UserRoleCode.JMAuctionAdministrator,
    UserRoleCode.RegulatoryComplianceAdministrator,
    UserRoleCode.MembershipAdministrator
  ],
  [ModuleName.ShoppingCart]: [
    ...defaultRoles,
  ],
  [ModuleName.Shipping]: [
    ...defaultRoles
  ],
  [ModuleName.Billing]: [
    ...defaultRoles,
  ],
  [ModuleName.Appraisals]: [
    ...defaultRoles,
    UserRoleCode.JMAppraisalAdministrator,
    UserRoleCode.JMAppraisalSales,
    UserRoleCode.JMCustomerService
  ],
  [ModuleName.PointOfSaleIntegration]: [
    ...defaultRoles,
    UserRoleCode.JMPointOfSaleAdministrator,
    UserRoleCode.JMCustomerService
  ],
  [ModuleName.DiamondMarketplace]: [
    ...defaultRoles,
    UserRoleCode.JMMarketplaceAdministrator
  ],
  [ModuleName.Memo]: [
    ...defaultRoles,
  ],
  [ModuleName.JewelerPages]: [
    ...defaultRoles,
    UserRoleCode.JewelerPagesAdministrator
  ],
  [ModuleName.Auction]: [
    ...defaultRoles,
    UserRoleCode.JMAuctionAdministrator,
    UserRoleCode.JMShippingAdministrator
  ],
  [ModuleName.PromoCode]: [
    ...defaultRoles,
    UserRoleCode.RegulatoryComplianceAdministrator,
    UserRoleCode.JMAuctionAdministrator,
    UserRoleCode.JewelerPagesAdministrator,
    UserRoleCode.JMPointOfSaleAdministrator,
    UserRoleCode.JMMarketplaceAdministrator,
    UserRoleCode.JMPartnerGatewayAdministrator,
    UserRoleCode.JMAppraisalAdministrator,
    UserRoleCode.JMShippingRegistrationAdministrator,
    UserRoleCode.JMPersonalLinesClaimsAdministrator,
    UserRoleCode.JMCommercialLinesAdministrator,
    UserRoleCode.JMShippingAdministrator,
    UserRoleCode.JMCarePlanAdministrator
  ],
  [ModuleName.Security]: [
    ...defaultRoles,
  ],
  [ModuleName.Entity]: [
    ...defaultRoles,
  ],
  [ModuleName.Party]: [
    ...defaultRoles,
  ],
  [ModuleName.BulkUpload]: [
    ...defaultRoles,
  ],
  [ModuleName.TermsAndConditions]: [
    ...defaultRoles,
  ],
  [ModuleName.RetailLocation]: [
    ...defaultRoles,
    UserRoleCode.JMCarePlanAdministrator,
    UserRoleCode.JMCarePlanCallCenter,
    UserRoleCode.JMCarePlanSales,
    UserRoleCode.JMCarePlanClaimProcessingManager,
    UserRoleCode.JMCarePlanClaimProcessor,
    UserRoleCode.JMCarePlanFinance
  ],
  [ModuleName.ProgramEnrollments]: [
    ...defaultRoles,
    UserRoleCode.JMPointOfSaleAdministrator,
    UserRoleCode.JMJewelerProgramsFinance
  ],
  [ModuleName.IncentivePrograms]: [
    ...defaultRoles,
    UserRoleCode.JMPointOfSaleAdministrator,
    UserRoleCode.JMJewelerProgramsFinance
  ]
};

export const moduleRoleAccess = {
  communication: moduleRoleAccessMap[ModuleName.Communication],
  risk: moduleRoleAccessMap[ModuleName.Risk],
  identity: moduleRoleAccessMap[ModuleName.Identity],
  jmIdentity: moduleRoleAccessMap[ModuleName.JMIdentity],
  membership: moduleRoleAccessMap[ModuleName.Membership],
  content: moduleRoleAccessMap[ModuleName.Content],
  payment: moduleRoleAccessMap[ModuleName.Payment],
  document: moduleRoleAccessMap[ModuleName.Document],
  account: moduleRoleAccessMap[ModuleName.Account],
  reporting: moduleRoleAccessMap[ModuleName.Reporting],
  shoppingCart: moduleRoleAccessMap[ModuleName.ShoppingCart],
  shipping: moduleRoleAccessMap[ModuleName.Shipping],
  billing: moduleRoleAccessMap[ModuleName.Billing],
  appraisals: moduleRoleAccessMap[ModuleName.Appraisals],
  pointOfSaleIntegration: moduleRoleAccessMap[ModuleName.PointOfSaleIntegration],
  diamondMarketplace: moduleRoleAccessMap[ModuleName.DiamondMarketplace],
  memo: moduleRoleAccessMap[ModuleName.Memo],
  jewelerPages: moduleRoleAccessMap[ModuleName.JewelerPages],
  auction: moduleRoleAccessMap[ModuleName.Auction],
  promoCode: moduleRoleAccessMap[ModuleName.PromoCode],
  security: moduleRoleAccessMap[ModuleName.Security],
  entity: moduleRoleAccessMap[ModuleName.Entity],
  party: moduleRoleAccessMap[ModuleName.Party],
  retailLocation: moduleRoleAccessMap[ModuleName.RetailLocation],
  programEnrollments: moduleRoleAccessMap[ModuleName.ProgramEnrollments],
  incentivePrograms: moduleRoleAccessMap[ModuleName.IncentivePrograms],
  bulkUpload:  moduleRoleAccessMap[ModuleName.BulkUpload],
  termsAndConditions:  moduleRoleAccessMap[ModuleName.TermsAndConditions],

  forModule(moduleName: ModuleName): UserRoleCode[] {
    return moduleRoleAccessMap[moduleName];
  },
};
